import { RequestDetailsForApprovalResponse, sendFollowUp } from "@api";
import { Modal } from "@components";
import { Button, Grid, Typography } from "@material-ui/core";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { State } from "@store";
import { useSelector } from "react-redux";

interface Props {
  detail: RequestDetailsForApprovalResponse;
  onViewHistory: () => void;
  onSendBack: () => void;
  onReject: () => void;
  onCancel: () => void;
  onApproveRequest: () => void;
  onViewAttestation: () => void;
  onResubmitRequest: () => void;
  isReSubmitting: boolean;
  prevPage?: string;
}

interface ButtonsDetail {
  show: boolean | null;
  label: string;
  content: React.ReactNode | null;
}

export const Buttons: React.FC<Props> = ({
  detail,
  onViewHistory,
  onSendBack,
  onReject,
  onApproveRequest,
  onViewAttestation,
  onCancel,
  onResubmitRequest,
  isReSubmitting,
  prevPage,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const location = useLocation<{ readonlyAttestation?: boolean }>();
  const user = useSelector((state: State) => state.user);

  let buttonViewHistory: ButtonsDetail = {
    show: true,
    label: "View History",
    content: undefined,
  };

  if (prevPage === "" || prevPage === undefined) {
    prevPage = "APPROVE";
  }

  let buttonSendBackRequestor: ButtonsDetail = {
    show:
      (detail.viewRole === "Approver" &&
        prevPage === "APPROVE" &&
        detail.request.status === "APPROVAL IN-PROGRESS" &&
        !detail.request.isSendBack) ||
      detail.request?.isDelegate === true,
    label: "Send Back To Requestor",
    content: undefined,
  };
  let buttonRejectRequest: ButtonsDetail = {
    show:
      (detail.viewRole === "Approver" &&
        prevPage === "APPROVE" &&
        detail.request.status === "APPROVAL IN-PROGRESS" &&
        !detail.request.isSendBack &&
        detail.request.userEmail !== user?.email) ||
      detail.request?.isDelegate === true,
    label: "Reject Request",
    content: undefined,
  };
  let buttonApproveRequest: ButtonsDetail = {
    show:
      (detail.viewRole === "Approver" &&
        prevPage === "APPROVE" &&
        detail.request.status === "APPROVAL IN-PROGRESS" &&
        !detail.request.isSendBack &&
        detail.request.userEmail !== user?.email) ||
      detail.request?.isDelegate === true,
    label: "Approve Request",
    content: undefined,
  };
  //btn
  let buttonViewAttestation: ButtonsDetail = {
    show:
      detail.request.isAttestationNeeded &&
      (detail.viewRole === "Requestor" ||
        detail.request.userEmail === user?.email),
    label:
      detail.request.userEmail === user?.email
        ? detail.request.isAttestationSigned
          ? "View Attestation"
          : "Sign Attestation"
        : location.state?.readonlyAttestation ||
          detail.request.isAttestationSigned
        ? "View Attestation"
        : "View Attestation",
    content: undefined,
  };
  let buttonSendFollowUp: ButtonsDetail = {
    show:
      (detail.viewRole === "Requestor" ||
        detail.request.requestorEmail === user?.email) &&
      detail.request.status === "APPROVAL IN-PROGRESS" &&
      !detail.request.isSendBack,
    label: "Send Follow Up",
    content: undefined,
  };
  let buttonReSubmitRequest: ButtonsDetail = {
    show:
      (detail.viewRole === "Requestor" ||
        detail.request.requestorEmail === user?.email) &&
      detail.request.status === "APPROVAL IN-PROGRESS" &&
      detail.request.isSendBack,
    label: "Re-Submit Request",
    content: undefined,
  };
  let buttonCancelRequest: ButtonsDetail = {
    show:
      (detail.viewRole === "Requestor" ||
        detail.request.requestorEmail === user?.email) &&
      detail.request.status === "APPROVAL IN-PROGRESS",
    label: "Cancel Request",
    content: undefined,
  };

  if (buttonViewHistory.show) {
    buttonViewHistory.content = (
      <Button variant="outlined" color="primary" onClick={onViewHistory}>
        {buttonViewHistory.label}
      </Button>
    );
  }

  if (buttonSendBackRequestor.show) {
    buttonSendBackRequestor.content = (
      <Button
        variant="outlined"
        color="primary"
        disableElevation
        onClick={onSendBack}
      >
        {buttonSendBackRequestor.label}
      </Button>
    );
  }

  if (buttonRejectRequest.show) {
    buttonRejectRequest.content = (
      <Button
        variant="contained"
        color="secondary"
        disableElevation
        onClick={onReject}
      >
        {buttonRejectRequest.label}
      </Button>
    );
  }

  if (buttonApproveRequest.show) {
    buttonApproveRequest.content = (
      <Button
        variant="contained"
        color="primary"
        disableElevation
        onClick={onApproveRequest}
      >
        {buttonApproveRequest.label}
      </Button>
    );
  }

  if (buttonViewAttestation.show) {
    buttonViewAttestation.content = (
      <Button
        variant="outlined"
        color="primary"
        onClick={onViewAttestation}
        disabled={isReSubmitting}
      >
        {buttonViewAttestation.label}
      </Button>
    );
  }

  if (buttonSendFollowUp.show) {
    buttonSendFollowUp.content = (
      <Button
        variant="outlined"
        color="primary"
        onClick={() =>
          sendFollowUp(detail.request.id).then(() => setOpen(true))
        }
      >
        {buttonSendFollowUp.label}
      </Button>
    );
  }

  if (buttonReSubmitRequest.show) {
    buttonReSubmitRequest.content = (
      <Button
        variant="outlined"
        color="primary"
        onClick={onResubmitRequest}
        disabled={isReSubmitting}
      >
        {isReSubmitting ? "Submitting..." : buttonReSubmitRequest.label}
      </Button>
    );
  }
  if (buttonCancelRequest.show) {
    buttonCancelRequest.content = (
      <Button variant="outlined" color="primary" onClick={onCancel}>
        {buttonCancelRequest.label}
      </Button>
    );
  }

  return (
    <Grid container justify="flex-end" direction="row" spacing={2}>
      <Grid item>{buttonViewHistory.content}</Grid>
      <Grid item>{buttonSendBackRequestor.content}</Grid>
      <Grid item>{buttonRejectRequest.content}</Grid>
      <Grid item>{buttonApproveRequest.content}</Grid>
      <Grid item>{buttonViewAttestation.content}</Grid>
      <Grid item>{buttonSendFollowUp.content}</Grid>
      <Grid item>{buttonReSubmitRequest.content}</Grid>
      <Grid item>{buttonCancelRequest.content}</Grid>
      <Modal
        title="Follow up"
        isOpen={open}
        closeModal={() => setOpen(false)}
        actions={[
          {
            label: "Dismiss",
            handler: () => {
              setOpen(false);
            },
          },
        ]}
      >
        <Typography align="center" paragraph>
          Follow up was sent successfully
        </Typography>
      </Modal>
    </Grid>
  );
};
